import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
} from 'react';
import {InputProps} from '../input/input';
import {Colors} from '@ui/interface';
import {InputNoBorder} from '../input/input';
import {TextAreaNoBorder} from '../textArea/textarea';
import nextId from 'react-id-generator';

export type FieldSize = 'default' | 'compact';

type FieldInputBaseProps = Omit<InputProps, 'size'>;

interface FieldInputProps extends FieldInputBaseProps {
  handleKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onHandleChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  checked?: boolean;
  useCompletedAsDisabled?: boolean;
  disabled?: boolean;
  fontWeight?: number;
  background?: Colors;
  colorString?: Colors;
  readonly?: boolean;
  size?: FieldSize;
}

export const FieldInput: FC<FieldInputProps> = forwardRef(
  (
    {
      onHandleChange,
      readonly,
      checked,
      useCompletedAsDisabled,
      disabled,
      colorString,
      fontWeight,
      background,
      size = 'default',
      ...props
    },
    inputRef: any,
  ) => {
    const resizeTextArea = useCallback(() => {
      if (inputRef.current && size !== 'compact') {
        const MIN_HEIGHT = 24;
        const textArea = inputRef.current;
        textArea.style.height = 'auto';
        const scrollHeight = Math.max(textArea.scrollHeight, MIN_HEIGHT);
        textArea.style.height = `${scrollHeight}px`;
      }
    }, [inputRef, size]);

    useEffect(() => {
      if (size !== 'compact') {
        resizeTextArea();
      }
    }, [resizeTextArea, size]);

    const baseProps = {
      'aria-label': 'input' as const,
      ref: inputRef,
      autoFocus: inputRef.current === document.activeElement,
      fontWeight,
      colorString: checked
        ? useCompletedAsDisabled
          ? 'textDark'
          : 'textMuted'
        : disabled
        ? 'textMedium'
        : colorString,
      background: disabled
        ? 'backgroundDark'
        : useCompletedAsDisabled
        ? 'backgroundLight'
        : background,
      id: nextId('fields'),
      readOnly: readonly || checked,
    };

    if (size === 'compact') {
      return (
        <InputNoBorder
          {...baseProps}
          {...(props as any)}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            onHandleChange && onHandleChange(event);
          }}
          style={{
            height: '24px',
            background: 'transparent',
            maxWidth: '100%',
            paddingBottom: '4px',
          }}
        />
      );
    }

    return (
      <TextAreaNoBorder
        {...baseProps}
        {...(props as any)}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
          onHandleChange && onHandleChange(event as any);
          resizeTextArea();
        }}
        style={{
          minHeight: '24px',
          background: 'transparent',
          padding: '0px 14px',
          maxWidth: '100%',
          lineHeight: '24px',
          fontSize: '1rem',
        }}
      />
    );
  },
);
